import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "@legal-500/legal500-design-system";
import { useQuery } from "@/hooks/useApi";
import { docParsing } from "@/api/docParsing.service";
import LoadingState from "@/components/LoadingState";
import EmptyState from "@/components/EmptyState";
import styles from "./DocumentParsing.module.css";

type JsonModalProps = {
  resultId: string | null;
  onClose: () => void;
};

export function JsonModal({ resultId, onClose }: JsonModalProps) {
  const { data, loading, error } = useQuery(
    () => (resultId ? docParsing.getResultById(resultId) : Promise.resolve(null)),
    [resultId],
  );

  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => clearTimeout(timeoutRef.current ?? undefined), []);

  if (!resultId) return null;

  const jsonContent = data?.result?.parsedJson
    ? JSON.stringify(data.result.parsedJson, null, 2)
    : null;

  const handleCopy = async () => {
    if (!jsonContent) return;
    clearTimeout(timeoutRef.current ?? undefined);
    try {
      await navigator.clipboard.writeText(jsonContent);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    } finally {
      timeoutRef.current = setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  return (
    <Modal isOpen={!!resultId} onClose={onClose} title="Parsed JSON" size="large">
      <div className={styles.jsonModalContent}>
        {loading && <LoadingState message="Loading parsed result..." />}
        {error && !loading && (
          <EmptyState variant="error" title="Failed to load result" message={error.message} />
        )}
        {!loading && !error && jsonContent && (
          <>
            <div className={styles.jsonActions}>
              <Button size="small" variant="outlined" onClick={handleCopy}>
                {copyState === "copied"
                  ? "Copied!"
                  : copyState === "failed"
                    ? "Copy failed"
                    : "Copy to clipboard"}
              </Button>
            </div>
            <pre className={styles.jsonPre}>{jsonContent}</pre>
          </>
        )}
        {!loading && !error && !jsonContent && (
          <EmptyState
            variant="empty"
            title="No parsed data"
            message="This document has no parsed JSON content."
          />
        )}
      </div>
    </Modal>
  );
}
